import { Button, Heading, Text, useIsBreakpointLargeAndUp, useIsDesignLanguage, DesignLanguage, useModal } from '@afterpaytouch/core'
import Image from 'next/image'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Route } from '../../../config/router'
import { BASE_PATH } from '../../../env'
import {
  selectPurchaseMethod,
  setCheckoutStatus,
  setSelectedPaymentType,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  useHasSeenPaymentTypesPBIRejected,
  useShowIdvModal,
} from '../../../state'
import { useCheckoutSizes } from '../../../utils/responsive'
import styles from './style.module.css'
import { Terms } from './Terms'
import { PaymentType } from '@afterpay/types'
import { CheckoutStatus, IdentityVerificationModalKey } from '../../../model'
import { usePBIRejectedText } from './helper'
import { TrackingEvent } from '../../../model/monitoring'
import { IdentityVerificationModal } from '../../IdentityVerificationModal'
import { useMonitoringWithEnduringEventProperties } from '../../../integrations/monitoring'
import { useTranslation } from '../../../utils/convergence'

export const PaymentTypesPBIRejected: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const isDesktop = useIsBreakpointLargeAndUp()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { showModal } = useModal(IdentityVerificationModalKey)
  const [lock, setLock] = useState<boolean>(false)
  const { extraSmallTextSize, sectionHeadingSize, buttonSize } = useCheckoutSizes()
  const paymentMethod = useAppropriatePaymentMethod()
  const { seenPaymentTypesPBIRejected, saveToTags } = useHasSeenPaymentTypesPBIRejected()
  const hasSeenPaymentTypesPBIRejected = useRef<boolean>(seenPaymentTypesPBIRejected)
  const showIDVModal = useShowIdvModal()

  const illustrationSrc = [BASE_PATH, 'images/payment-types-pbi-rejected-illustration.svg'].join('/')

  const heading = !hasSeenPaymentTypesPBIRejected.current ? t('consumerLending:paymentTypesB:firstViewHeading') : t('consumerLending:paymentTypesB:heading')
  const subheading = usePBIRejectedText()
  const aprDisclaimer = t('consumerLending:paymentTypesB:aprDisclaimer')
  const byTheWay = t('consumerLending:paymentTypesB:byTheWay')
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const handleContinueClick = (): void => {
    setLock(true)

    dispatch(setSelectedPaymentType(PaymentType.PCL))

    logEvent(TrackingEvent.PRESSED_CL_ENTRY_PAGE_CONTINUE_BUTTON, {
      paymentType: PaymentType.PCL,
    })

    const hasPaymentMethod = paymentMethod !== null
    if (showIDVModal) {
      showModal()
    } else if (hasPaymentMethod) {
      dispatch(selectPurchaseMethod(paymentMethod))
      router.push(Route.PAYMENT_OPTIONS)
    } else {
      dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
      router.push(Route.PAYMENT_OPTIONS)
    }
  }

  useEffect(() => {
    if (!seenPaymentTypesPBIRejected) {
      saveToTags()
    }
  }, [seenPaymentTypesPBIRejected])

  useEffect(() => {
    router.prefetch(Route.PAYMENT_OPTIONS)
  }, [router])

  return (
    <>
      <div className={isDesktop ?? false ? styles.PBIRejectedWrapperDesktop : styles.PBIRejectedWrapperMobile}>
        <Heading size='XXL'>{heading}</Heading>
        <div className={styles.PBIRejectedSubheader}>
          <Text size='S'>{subheading}</Text>
        </div>
        <div className={styles.paymentTypesAPRDisclaimer}></div>
        <Text size={extraSmallTextSize} color='Gray50'>
          {aprDisclaimer}
        </Text>
        <div className={styles.PBIRejectedIllustration}>
          <Image
            data-testid='payment-types-pbi-rejected-scene'
            src={illustrationSrc}
            alt='Payment Types PBI Rejected Scene'
            width='206'
            height='220'
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </div>
        <div className={styles.PBIRejectedByTheWay}>
          <Heading size={sectionHeadingSize}>{byTheWay}</Heading>
        </div>
        <Terms />
        <div className={styles.PBIRejectedAgreeButton}>
          <Button
            kind={isArcadeDesignLanguage ? 'Primary' : 'Secondary'}
            loading={lock}
            disabled={lock}
            type='button'
            padding='Fluid'
            size={buttonSize}
            onClick={handleContinueClick}
            testNameSpace='continue'
          >
            {t('consumerLending:paymentTypesB:continue')}
          </Button>
        </div>
      </div>
      <IdentityVerificationModal setClicked={setLock} />
    </>
  )
}
